<template>
  <div class="purchase">
    <div class="purchase__top mb-1 mb-md-1-5 mb-xlg-2">
      <h1 class="text-md-center" data-cy-title>
        {{ productDocument.title }}
      </h1>

      <image-picker
        v-if="linkedProducts.length > 0"
        id="linked-products-picker"
        v-model="selectedDimensions.linkedProduct"
        :elements="linkedProducts"
        class="d-md-none mb-1-5 mt-2"
        @input="onLinkedProductChange"
      />

      <image-picker
        v-if="hasThirdDimension && canSelectThirdDimension"
        :id="`${thirdDimensionName}-picker-mobile`"
        v-model="selectedDimensions.third"
        :elements="thirdDimensions"
        class="d-md-none mb-1-5 mt-2"
        @input="onThirdDimensionChange"
      />

      <color-picker
        v-if="
          colorsDimension.length > 1 ||
          (canSelectThirdDimension && colorsDimension.length > 0)
        "
        id="colorPickerMobile"
        v-model="selectedDimensions.color"
        :colors="colorsDimension"
        class="d-md-none"
        :product-document="productDocument"
        :skeleton="loading"
        @input="onColorChange"
      />

      <material-picker
        v-if="shouldShowThirdDimension && !canSelectThirdDimension"
        :id="`${thirdDimensionName}-picker-mobile`"
        v-model="selectedDimensions.third"
        :materials="thirdDimensions"
        :label="productDocument.third_option_label"
        class="d-md-none"
      />

      <product-price
        v-if="selectedVariant"
        :price="selectedVariant.price"
        :compare-at-price="selectedVariant.compareAtPrice"
        :variant="selectedVariant.available"
        :currency="shopifyProduct.currency"
        :class="{
          'mb-0-4': withTestimonials,
          'mb-1': !withTestimonials,
          'mt-md-2 mb-1-5': hasPrecoOrStockLink,
        }"
        :selectable="hasPrecoOrStockLink"
        :product-document="productDocument"
        :product-status="productStatus"
        :shopify-product="shopifyProduct"
        :selected-color="selectedDimensions.color"
        :on-refresh="!isRefreshed"
      />
      <product-price
        v-else
        :price="
          hasThirdDimension && selectedDimensions.third
            ? shopifyProduct.priceByDimension(
                thirdDimensionName,
                selectedDimensions.third
              )
            : selectedDimensions.color !== ''
            ? shopifyProduct.priceByDimension('color', selectedDimensions.color)
            : shopifyProduct.price
        "
        :compare-at-price="
          hasThirdDimension && selectedDimensions.third
            ? shopifyProduct.compareAtPriceByDimension(
                thirdDimensionName,
                selectedDimensions.third
              )
            : selectedDimensions.color !== ''
            ? shopifyProduct.compareAtPriceByDimension(
                'color',
                selectedDimensions.color
              )
            : shopifyProduct.compareAtPrice
        "
        :variant="shopifyProduct.available"
        :currency="shopifyProduct.currency"
        :class="{
          'mb-0-4': withTestimonials,
          'mb-1': !withTestimonials,
          'mt-md-2 mb-1-5': hasPrecoOrStockLink,
        }"
        :selectable="hasPrecoOrStockLink"
        :product-document="productDocument"
        :product-status="productStatus"
        :shopify-product="shopifyProduct"
        :selected-color="selectedDimensions.color"
        :on-refresh="!isRefreshed"
      />

      <purchase-testimonials
        v-if="withTestimonials"
        :number-of="productDocument.rating_number"
        :rating="productDocument.rating_average"
        :has-block="hasTestimonialsBlock"
        class="mb-0-6 mt-1 mt-md-0"
      />

      <div class="d-flex justify-center text-left mb-0">
        <div>
          <ul class="purchase__bullets mb-0">
            <li
              v-for="el in productDocument.bullets"
              :key="el"
              class="text-small"
              data-cy-bullet-el
            >
              {{ el }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <image-picker
      v-if="linkedProducts.length > 0"
      id="linked-products-picker"
      v-model="selectedDimensions.linkedProduct"
      :elements="linkedProducts"
      class="d-none d-md-block mb-2"
      @input="onLinkedProductChange"
    />

    <image-picker
      v-if="hasThirdDimension && canSelectThirdDimension"
      :id="`${thirdDimensionName}-picker`"
      v-model="selectedDimensions.third"
      :elements="thirdDimensions"
      class="d-none d-md-block mb-2"
      @input="onThirdDimensionChange"
    />

    <color-picker
      v-if="
        colorsDimension.length > 1 ||
        (canSelectThirdDimension && colorsDimension.length > 0)
      "
      id="colorPicker"
      v-model="selectedDimensions.color"
      :colors="colorsDimension"
      class="d-none d-md-block"
      :product-document="productDocument"
      :skeleton="loading"
      @input="onColorChange"
    />

    <material-picker
      v-if="shouldShowThirdDimension && !canSelectThirdDimension"
      :id="`${thirdDimensionName}-picker`"
      v-model="selectedDimensions.third"
      :materials="thirdDimensions"
      :label="productDocument.third_option_label"
      class="d-none d-md-block mb-2"
    />

    <transition name="fade" mode="out-in">
      <div
        v-if="hasSizePicker"
        key="size-picker"
        class="purchase__size-picker mb-1"
      >
        <value-picker
          ref="sizePicker"
          v-model="selectedDimensions.size"
          :values="sizesDimension"
          :skeleton="loading"
          :error="errors.size !== ERRORS_TYPE.NONE"
          data-cy-size-picker
          @input="updateState(true)"
        />

        <transition name="appear-top">
          <error-text
            v-if="errors.size !== ERRORS_TYPE.NONE"
            :text="$t(`purchase.errors.size.${errors.size}`)"
            class="mt-0-6 mb-0-2"
          />
        </transition>
      </div>
    </transition>

    <transition name="fade-from-top" mode="out-in">
      <div
        v-if="showInventoryLeft"
        class="almost-sold-out mb-1-5 d-flex align-center justify-center"
      >
        <icon name="info-circle" />
        <p
          class="mb-0 pl-0-4"
          v-html="
            $tc('product.almost_sold_out', selectedVariant.quantityAvailable, {
              number: selectedVariant.quantityAvailable,
            })
          "
        />
      </div>
    </transition>

    <div
      v-if="productDocument.size_guide"
      ref="sizeGuideLinks"
      class="d-flex align-center justify-center gap-1-5"
      :class="{
        'size-guide-highlighted': productDocument.highlight_size_guide,
      }"
    >
      <a
        :href="`#${$t('purchase.size-guide')}`"
        class="link-cta link--size-guide lh-1"
        @click.prevent="sizeGuide()"
      >
        <ceintre-icon />
        <span>{{ $t('purchase.size-guide') }}</span>
      </a>

      <div id="kleep-module-sizing"></div>

      <!-- <client-only>
        <div class="d-flex" @click="trackFitle" v-html="fitleTag" />
      </client-only> -->
    </div>

    <div class="purchase__bottom mt-2 mt-xlg-3">
      <transition name="fade" mode="out-in">
        <div v-if="productIsOnSale && !soldOut" key="buy">
          <primary-button
            id="AddToCart"
            :text="actionText"
            align="center"
            with-arrow
            type="button"
            :loading="cartLoading"
            :error="onError"
            :skeleton="!isRefreshed"
            :class="hasPrecoOrStockLink ? 'mb-1-5' : 'mb-1'"
            data-cy-add-to-cart
            @click="addToCart"
          ></primary-button>

          <transition name="appear-top">
            <error-block
              v-if="errors.addToCart !== ERRORS_TYPE.NONE"
              class="mb-1"
            >
              <p
                class="mb-0"
                v-html="$t(`purchase.errors.addToCart.${errors.addToCart}`)"
              />
            </error-block>
          </transition>

          <div
            v-if="showDeliveryUnderAddToCart"
            class="purchase__delivery text-small text-center mb-1"
          >
            <span v-if="productStatus === STATUS.PRECOMMANDE">
              {{ $t('purchase.estimatedDelivery') }}
              <span
                v-if="!isRefreshed"
                class="skeleton skeleton--bar d-inline-block"
                style="width: 100px; vertical-align: middle"
                >&nbsp;</span
              >
              <span v-else
                >{{ $t(deliveryText.ref, deliveryText.options)
                }}<span v-if="deliveryText.ref === 'noDays'">{{
                  $t('purchase.contactCare')
                }}</span></span
              >
            </span>
            <span v-else>
              {{ $t('purchase.delivery') }}&nbsp;{{ $t('purchase.fewDays') }}
            </span>
          </div>

          <div
            v-if="
              productStatus === STATUS.PRECOMMANDE ||
              productStatus === STATUS.STOCK
            "
            class="purchase__informations d-flex align-end justify-center"
          >
            <div v-if="showTimer" class="text-center child">
              <count-down
                :end="
                  shopifyProduct.hasDates
                    ? shopifyProduct.endDate
                    : productDocument.dates.end
                "
                :over-text="$t('purchase.timer.over')"
                :skeleton="!isRefreshed"
                class="text-big w-600"
              >
                <div
                  class="text-caption text-small w-400 d-block lh-1-2 pt-0-2"
                >
                  {{
                    $t(
                      productStatus === STATUS.STOCK
                        ? 'purchase.stockEnd'
                        : 'purchase.precoEnd'
                    )
                  }}
                </div>
              </count-down>
            </div>
            <div
              v-else-if="showNextBatch"
              class="text-center child child--with-batch mt-1"
            >
              <count-down
                :before-text="$t('purchase.timer.only')"
                :end="productDocument.current_batch_date_end"
                :over-text="$t('purchase.timer.over')"
                :skeleton="!isRefreshed"
                class="text-big w-600"
                show-unit
                @mounted="initTootip"
              >
                <div
                  ref="tooltip"
                  class="text-caption text-small w-400 d-block lh-1-2 rich-text pt-0-2"
                >
                  <span
                    v-if="!isRefreshed"
                    class="skeleton skeleton--bar d-inline-block"
                    style="width: 100px; vertical-align: middle"
                    >&nbsp;</span
                  >
                  <span
                    v-else
                    class="d-block tooltip"
                    v-html="$t('purchase.batchEnd', deliveryText.options)"
                  >
                  </span>
                </div>
              </count-down>
            </div>
            <div
              v-if="productStatus === STATUS.PRECOMMANDE"
              class="child text-center"
            >
              <div class="text-big mb-0-5">
                <number-animated
                  ref="number"
                  :from="numberOfSells.from"
                  :to="numberOfSells.to"
                  :duration="0.5"
                  class="w-600"
                  @complete="numberOfSells.from = numberOfSells.to"
                />
              </div>
              <div class="text-caption d-block lh-1-2 pt-0-2">
                {{ $t('purchase.nbPreco') }}
              </div>
            </div>
          </div>

          <a
            v-if="
              productDocument.delivery_and_returns &&
              productDocument.delivery_and_returns._ref
            "
            href="#delivery-and-exchange"
            class="purchase__delivery link-cta mt-1 pt-0-2"
            @click.prevent="exchange()"
          >
            <box-icon />
            <span>{{ $t('purchase.exchange') }}</span>
          </a>
        </div>

        <div
          v-else
          key="create-alert"
          class="purchase__create-alert text-center"
        >
          <create-alert
            id="purchase"
            :product="bisProduct"
            :title="createAlertTitle"
            :text="createAlertText"
            :date="createAlertDate"
            :origin="ORIGIN.PRODUCT"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import slugify from 'slugify'

import { differenceInDays, isFuture, isPast } from 'date-fns'
import services from '@/services'

import ProductPrice from '@/components/Product/ProductPrice'
import PrimaryButton from '@/components/buttons/Primary'
import ColorPicker from '@/components/picker/ColorPicker'
import ValuePicker from '@/components/picker/ValuePicker'
import Icon from '@/components/icons/Icon'

import PurchaseTestimonials from '@/components/Purchase/PurchaseTestimonials'
import CreateAlert from '@/components/Product/CreateAlert'
import BoxIcon from '@/components/icons/Box'
import CeintreIcon from '@/components/icons/Ceintre'

import ErrorText from '@/components/errors/Text'
import ErrorBlock from '@/components/errors/Block'

import DeliveryMixin from '@/mixins/deliveryText'

import {
  STATUS,
  ORIGIN,
  SHOPIFY_TYPES,
  MARKET_TYPES,
  TYPES,
  ERRORS_TYPE,
  SS_UPSELLS_ITEMS,
} from '@/const'
import { EVENTS } from '@/const/events'

import normalize from '@/utils/normalize'
import ShopifyBase64 from '@/utils/shopifyBase64'
import {
  isProductSaleable,
  isProductWillSoonBeForSale,
  PRODUCT_COLOR_STATUS,
  PRODUCT_TYPE,
} from '@/entities/sanity/product'
import ShopifyProduct from '@/entities/shopify/Product'
import { getSanityBunnyImageLink } from '@/utils/image'

export default {
  name: 'Purchase',
  components: {
    ProductPrice,
    PrimaryButton,
    ColorPicker,
    ValuePicker,
    MaterialPicker: () => import('@/components/picker/MaterialPicker'),
    ImagePicker: () => import('@/components/picker/ImagePicker'),
    CreateAlert,
    PurchaseTestimonials,
    BoxIcon,
    Icon,
    CeintreIcon,
    ErrorText,
    ErrorBlock,
    CountDown: () => ({
      component: import(
        '@/components/countdown/CountDown' /* webpackChunkName: 'countdown' */
      ),
    }),
    NumberAnimated: () => ({
      component: import(
        '@/components/number/Number' /* webpackChunkName: 'animated_number' */
      ),
    }),
  },
  mixins: [DeliveryMixin],
  props: {
    productDocument: { type: Object, required: true },
    shopifyProduct: { type: Object, required: true },
    withTestimonials: { type: Boolean, default: false },
    productVariantUpdated: { type: Boolean, required: true },
    additionnalInventory: { type: Number, default: 0 },
    sliderAbtestColor: { type: String, default: null },
  },
  data() {
    return {
      selectedDimensions: {
        color: '',
        size: '',
        third: '',
        linkedProduct: '',
      },
      loading: true,
      isRefreshed: false,
      numberOfSells: { from: 0, to: 0 },
      errors: {
        size: ERRORS_TYPE.NONE,
        addToCart: ERRORS_TYPE.NONE,
      },
      STATUS,
      MARKET_TYPES,
      ORIGIN,
      TYPES,
      ERRORS_TYPE,
    }
  },
  computed: {
    ...mapGetters({
      cart: 'cart/cart',
      cartLoading: 'cart/isLoading',
      authenticated: 'auth/authenticated',
      customer: 'auth/customer',
      connectedEmail: 'subscriptions/connectedEmail',
    }),
    isSaisonnier() {
      return (
        this.productDocument &&
        this.productDocument.type === PRODUCT_TYPE.SAISONNIER
      )
    },
    productIsOnSale() {
      if (this.shopifyProduct.hasDates) {
        return this.shopifyProduct.isSaleableAsStock
      }

      return isProductSaleable(this.productDocument)
    },
    productStatus() {
      if (this.shopifyProduct.hasDates) {
        return this.shopifyProduct.isSaleableAsStock
          ? STATUS.STOCK
          : STATUS.AGENDA
      }

      return this.productDocument.status
    },

    bisProduct() {
      return {
        productSlug: this.productDocument.uid,
        productName: this.productDocument.title,
        productId: this.shopifyProduct.id,
      }
    },
    actionText() {
      if (this.productStatus === STATUS.STOCK) {
        return this.$t('action.buy')
      }

      return this.$t('action.preorder')
    },
    hasSizePicker() {
      if (this.hasThirdDimension) {
        return (
          this.productIsOnSale &&
          this.shopifyProduct.isMixedDimensionsAvailable({
            color: this.selectedDimensions.color,
            [this.thirdDimensionName]: this.selectedDimensions.third,
          })
        )
      }

      return (
        this.productIsOnSale &&
        this.shopifyProduct.isDimensionAvailable(
          'color',
          this.selectedDimensions.color
        )
      )
    },
    createAlertTitle() {
      if (this.productIsOnSale) {
        if (this.shopifyProduct.isDimensionExist(this.thirdDimensionName)) {
          const mixedDimension = {
            color: this.selectedDimensions.color,
          }
          mixedDimension[this.thirdDimensionName] =
            this.selectedDimensions.third
          if (!this.shopifyProduct.isMixedDimensionsAvailable(mixedDimension)) {
            return this.$t('product.color-not-available')
          }
        }
        if (
          !this.shopifyProduct.isDimensionAvailable(
            'color',
            normalize(this.selectedDimensions.color)
          )
        ) {
          return this.$t('product.color-not-available')
        }

        if (this.soldOut) {
          return this.$t('product.size-not-available')
        }
      }

      return this.$t('product.back-one-day')
    },
    createAlertText() {
      if (this.productIsOnSale && this.soldOut) {
        if (ShopifyProduct.willBeInStock(this.shopifyProduct)) {
          return this.$t('product.soldout_stock')
        }

        if (this.isSaisonnier) {
          return this.$t('product.soldout-saisonnier')
        }
        return this.$t('product.soldout')
      }

      if (ShopifyProduct.willBeInStock(this.shopifyProduct)) {
        return this.$t('product.receive-alert-stock')
      }

      return this.$t('product.receive-alert-preorder')
    },
    createAlertDate() {
      if (!this.productIsOnSale) {
        const now = new Date()

        if (this.shopifyProduct.startDate) {
          return this.shopifyProduct.startDate > now
            ? this.shopifyProduct.startDate
            : false
        }

        if (isProductWillSoonBeForSale(this.productDocument)) {
          return this.productDocument.dates.start
        }
      }

      return false
    },
    hasPrecoOrStockLink() {
      return (
        this.productDocument.product_as_preco_or_stock !== null &&
        this.productDocument.product_as_preco_or_stock.uid !== null
      )
    },
    showDeliveryUnderAddToCart() {
      return !this.showNextBatch && !this.hasPrecoOrStockLink
    },
    delivery() {
      return this.shopifyProduct.metafield
        ? new Date(this.shopifyProduct.metafield)
        : undefined
    },
    deliveryText() {
      if (
        this.productStatus === STATUS.PRECOMMANDE &&
        this.delivery &&
        !isNaN(this.delivery.getTime())
      ) {
        return this.getDeliveryFromDate(this.delivery)
      }

      if (this.productStatus === STATUS.STOCK) {
        return { ref: 'purchase.fewDays', options: {} }
      }

      return { ref: 'purchase.noDays', options: {} }
    },
    nextBatchDeliveryOptions() {
      if (
        this.delivery !== undefined &&
        this.productDocument.next_delivery_date
      ) {
        return {
          ...this.getDeliveryFromDate(this.delivery, 'current').options,
          ...this.getDeliveryFromDate(
            this.productDocument.next_delivery_date,
            'next'
          ).options,
        }
      }

      return {}
    },
    colorsDimension() {
      const cc = []

      for (const color of this.productDocument.colors) {
        if (
          this.shopifyProduct.isDimensionHasValue(
            'color',
            this.getColorFromColorKey(color._key)
          )
        ) {
          let shouldAddColor = true
          if (this.canSelectThirdDimension && this.selectedDimensions.third) {
            const colorsOfSelectedThirdDimension =
              this.shopifyProduct.getDimensionsByDimension(
                this.thirdDimensionName,
                this.selectedDimensions.third,
                'color'
              )
            shouldAddColor = colorsOfSelectedThirdDimension.includes(
              this.getColorFromColorKey(color._key)
            )
          }

          if (!cc.some((c) => c.value === color._key) && shouldAddColor) {
            const label = !this.canSelectThirdDimension
              ? this.productDocument.shopify_product.colorOptions.find(
                  (colorOption) => color._key.endsWith(colorOption._key)
                )?.name || color.label
              : this.productDocument.shopify_product.colorOptions.find(
                  (colorOption) => colorOption._key === color._key
                )?.name || color.label

            const c = {
              value: this.getColorFromColorKey(color._key),
              label,
              isNew: color.color_status === PRODUCT_COLOR_STATUS.NEW,
              isLeavingSoon:
                color.color_status === PRODUCT_COLOR_STATUS.LEAVING_SOON,
              image: {
                url: getSanityBunnyImageLink(color.thumbnail, {
                  width: 100,
                  height: 100,
                }),
                dimensions: {
                  width: 100,
                  height: 100,
                },
              },
            }

            if (this.canSelectThirdDimension) {
              const mixedDimension = {
                color: c.value,
              }
              mixedDimension[this.thirdDimensionName] =
                this.selectedDimensions.third

              c.available =
                this.shopifyProduct.isMixedDimensionsAvailable(mixedDimension)

              c[this.thirdDimensionName] =
                this.shopifyProduct.getDimensionsByDimension(
                  'color',
                  c.value,
                  this.thirdDimensionName
                )[0]
            } else {
              c.available = this.shopifyProduct.isDimensionAvailable(
                'color',
                c.value
              )

              if (this.hasThirdDimension) {
                c[this.thirdDimensionName] =
                  this.shopifyProduct.getDimensionsByDimension(
                    'color',
                    c.value,
                    this.thirdDimensionName
                  )[0]
              }
            }

            cc.push(c)
          }
        }
      }

      return cc
    },
    sizesDimension() {
      if (this.selectedDimensions.color !== '') {
        if (this.hasThirdDimension) {
          return this.shopifyProduct.filterVariantsByDimensions([
            {
              dimension: 'color',
              value: this.selectedDimensions.color,
            },
            {
              dimension: this.thirdDimensionName,
              value: this.selectedDimensions.third,
            },
          ])
        }
        return this.shopifyProduct.filterVariantsByDimension(
          'color',
          this.selectedDimensions.color
        )
      }

      return []
    },
    hasThirdDimension() {
      return (
        this.productDocument.shopify_product.options.length > 2 &&
        this.productDocument.shopify_product.options.some(
          (o) => o.position === 3
        )
      )
    },
    thirdDimensionName() {
      if (this.hasThirdDimension) {
        return slugify(
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          ).name,
          { lower: true, strict: true }
        )
      }

      return ''
    },
    thirdDimensions() {
      const dd = []

      if (this.hasThirdDimension) {
        for (const d of this.productDocument.third_options.list) {
          if (
            this.shopifyProduct.isDimensionHasValue(
              this.thirdDimensionName,
              slugify(d.name, { lower: true, strict: true })
            )
          ) {
            dd.push({
              value: normalize(d.name),
              label: d.name,
              desc: d.desc,
              image: d.image,
              available: this.shopifyProduct.isDimensionAvailable(
                this.thirdDimensionName,
                normalize(d.name)
              ),
            })
          }
        }
      }

      return dd
    },
    canSelectThirdDimension() {
      return (
        this.hasThirdDimension &&
        this.productDocument.third_options.layout === 'as_selector'
      )
    },
    shouldShowThirdDimension() {
      return this.hasThirdDimension && this.productDocument.third_option_label
    },
    selectedVariant() {
      if (this.hasThirdDimension) {
        return this.shopifyProduct.variants.find(
          (v) =>
            v.opt.color.value === this.selectedDimensions.color &&
            v.opt.size.value === this.selectedDimensions.size &&
            v.opt[this.thirdDimensionName].value ===
              this.selectedDimensions.third
        )
      }

      return this.shopifyProduct.variants.find(
        (v) =>
          v.opt.color.value === this.selectedDimensions.color &&
          v.opt.size.value === this.selectedDimensions.size
      )
    },
    soldOut() {
      if (this.selectedVariant) {
        return this.selectedVariant && !this.selectedVariant.available
      }

      if (this.selectedDimensions.color === '') {
        // On ne peut pas savoir si le produit est sold out si aucune couleur n'est sélectionnée
        return false
      }

      if (this.hasThirdDimension) {
        return !this.shopifyProduct.isMixedDimensionsAvailable({
          color: this.selectedDimensions.color,
          [this.thirdDimensionName]: this.selectedDimensions.third,
        })
      }

      return !this.shopifyProduct.isDimensionAvailable(
        'color',
        this.selectedDimensions.color
      )
    },
    orderType() {
      if (this.productStatus === STATUS.PRECOMMANDE) {
        return 'preorder'
      }
      return 'stock'
    },
    onError() {
      if (!this.soldOut) {
        if (
          this.errors.size !== ERRORS_TYPE.NONE ||
          this.errors.addToCart !== ERRORS_TYPE.NONE
        ) {
          return true
        }
      }

      return false
    },
    showTimer() {
      if (
        this.shopifyProduct.hasDates &&
        this.shopifyProduct.isSaleableAsStock
      ) {
        return this.shouldShowTimer(this.shopifyProduct.endDate)
      }

      if (this.productDocument.dates.end) {
        return this.shouldShowTimer(this.productDocument.dates.end)
      }

      return false
    },
    showNextBatch() {
      if (
        this.productDocument.current_batch_date_end &&
        this.productDocument.next_delivery_date
      ) {
        return (
          isFuture(this.productDocument.current_batch_date_end) &&
          differenceInDays(
            this.productDocument.current_batch_date_end,
            new Date()
          ) <= 14
        )
      }
      return false
    },
    showInventoryLeft() {
      if (this.productStatus === STATUS.STOCK) {
        return (
          this.selectedVariant &&
          this.selectedVariant.available &&
          this.selectedVariant.quantityAvailable <
            process.env.ALMOST_SOLD_OUT_LIMIT
        )
      }

      return false
    },
    linkedProducts() {
      if (
        this.productDocument.has_linked_product &&
        this.productDocument.linked_products &&
        this.productDocument.linked_products.length > 0
      ) {
        return this.productDocument.linked_products
          .filter((lp) => lp && lp.product?.uid)
          .map((lp) => ({
            value: lp.product.uid,
            label: lp.label,
            image: lp.image,
          }))
      }

      return []
    },

    productHasBeenAccessedFromUpsell() {
      if (process.client) {
        let upsellsId = window.sessionStorage.getItem(SS_UPSELLS_ITEMS)
        if (upsellsId) {
          upsellsId = JSON.parse(upsellsId)
          return (
            upsellsId.includes(this.shopifyProduct.id) ||
            this.$route.query.upsell === 'true'
          )
        }
      }
      return this.$route.query.upsell === 'true'
    },
    hasTestimonialsBlock() {
      if (this.withTestimonials && this.productDocument.blocks) {
        return this.productDocument.blocks.some((block) => {
          if (block._type === 'avisClientBlock') {
            if (block.visibility !== null) {
              return block.visibility
            }

            return block.defaultVisibility
          }

          return false
        })
      }

      return false
    },
  },
  created() {
    this.selectedDimensions.linkedProduct = this.productDocument.uid

    this.numberOfSells = {
      from:
        this.shopifyProduct.totalInventory + this.productDocument.stock_initial,
      to:
        this.shopifyProduct.totalInventory + this.productDocument.stock_initial,
    }

    if (!this.$route.query.color && !this.$route.query.variant) {
      if (this.hasThirdDimension && this.canSelectThirdDimension) {
        this.selectFirstAvailableThirdDimension()
      }

      this.selectFirstAvailableColor()
      this.updateState(false)
    }
  },
  mounted() {
    this.selectFirstAvailableVariant()

    this.$nextTick().then((_) => {
      this.loading = false
    })

    if (this.productVariantUpdated) {
      this.isRefreshed = true
      this.$nextTick().then((_) => {
        this.$track.product(
          this.productDocument.title,
          this.shopifyProduct,
          this.sizesDimension.length > 0 ? this.sizesDimension : []
        )
      })
    } else {
      this.$nuxt.$on(
        EVENTS.PRODUCT_VARIANT_UPDATED,
        this.onProductVariantUpdated
      )
    }

    this.initKleep()
  },
  beforeDestroy() {
    this.$nuxt.$off(
      EVENTS.PRODUCT_VARIANT_UPDATED,
      this.onProductVariantUpdated
    )

    const kleep = document.getElementById('kleepWidget')
    if (kleep) {
      kleep.remove()
    }
  },
  methods: {
    ...mapActions({
      addItemToCart: 'cart/addItems',
    }),
    /**
     * Permet d'extraire la couleur de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La couleur correspondante
     */
    getColorFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[1] : colorKey
    },
    /**
     * Permet d'extraire la troisième dimension de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La third correspondante
     */
    getThirdFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[0] : colorKey
    },
    shouldShowTimer(date) {
      const now = new Date()
      return isPast(date) || differenceInDays(date, now) <= 10
    },
    onProductVariantUpdated() {
      this.$nextTick().then(() => {
        this.selectFirstAvailableVariant()
        this.numberOfSells.to =
          this.shopifyProduct.totalInventory +
          this.productDocument.stock_initial
        this.isRefreshed = true

        this.$track.product(
          this.productDocument.title,
          this.shopifyProduct,
          this.sizesDimension.length > 0 ? this.sizesDimension : []
        )
      })
    },
    async initTootip() {
      if (this.$refs.tooltip) {
        try {
          const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
          await import(
            '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
          )
          await import(
            'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
          )

          tippy.default(this.$refs.tooltip, {
            theme: 'dark',
            placement: 'top',
            animation: 'scale',
            arrow: true,
            onShow: (instance) => {
              if (this.isRefreshed) {
                return instance.setContent(
                  this.$t(
                    'purchase.precoEndTooltip',
                    this.nextBatchDeliveryOptions
                  )
                )
              }

              instance._wait = setInterval((_) => {
                if (this.isRefreshed) {
                  instance.setContent(
                    this.$t(
                      'purchase.precoEndTooltip',
                      this.nextBatchDeliveryOptions
                    )
                  )
                  clearInterval(instance._wait)
                } else {
                  instance.setContent(this.$t('loading'))
                }
              }, 500)
            },
            onHidden(instance) {
              clearInterval(instance._wait)
            },
          })
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error while loading tippy.js', e)
        }
      }
    },
    async selectFirstAvailableVariant() {
      if (this.colorsDimension.length === 0) {
        return
      }

      if (this.$route.query.variant) {
        const variant = this.shopifyProduct.variants.find(
          (v) => v.id === parseInt(this.$route.query.variant)
        )

        if (variant && variant.opt.color && variant.opt.size) {
          this.selectedDimensions = {
            color: variant.opt.color.value,
            size: variant.opt.size.value,
            third: variant.opt[this.thirdDimensionName]
              ? variant.opt[this.thirdDimensionName].value
              : '',
          }

          this.updateState(false)
          return
        }
      }

      if (
        (this.$route.query.color &&
          this.shopifyProduct.isDimensionHasValue(
            'color',
            normalize(this.$route.query.color)
          ) &&
          this.colorsDimension.some(
            (c) => c.value === normalize(this.$route.query.color)
          )) ||
        (this.$route.query[this.thirdDimensionName] &&
          this.shopifyProduct.isDimensionHasValue(
            this.thirdDimensionName,
            slugify(this.$route.query[this.thirdDimensionName])
          ))
      ) {
        let variant

        if (
          this.$route.query.color &&
          this.$route.query[this.thirdDimensionName]
        ) {
          variant = this.shopifyProduct.variants.find(
            (v) =>
              v.opt.color.value === normalize(this.$route.query.color) &&
              v.opt[this.thirdDimensionName].value ===
                normalize(this.$route.query[this.thirdDimensionName])
          )
        } else if (this.$route.query.color) {
          variant = this.shopifyProduct.variants.find(
            (v) => v.opt.color.value === slugify(this.$route.query.color)
          )
        } else if (this.$route.query[this.thirdDimensionName]) {
          variant = this.shopifyProduct.variants.find(
            (v) =>
              v.opt[this.thirdDimensionName].value ===
              slugify(this.$route.query[this.thirdDimensionName])
          )
        }

        this.selectedDimensions.color = variant.opt.color.value

        if (variant.opt[this.thirdDimensionName]) {
          this.selectedDimensions.third =
            variant.opt[this.thirdDimensionName].value
        }

        this.updateState(false)
        return
      }

      if (this.hasThirdDimension && this.canSelectThirdDimension) {
        this.selectFirstAvailableThirdDimension()

        await this.$nextTick()
      }

      this.selectFirstAvailableColor()

      await this.$nextTick()

      if (this.shopifyProduct.productDimensions.size.length === 1) {
        this.selectFirstAvailableSize()
      }
      this.updateState(false)
    },
    selectFirstAvailableThirdDimension() {
      const firstAvailableThirdDimension = this.thirdDimensions.find(
        (d) => d.available
      )

      if (firstAvailableThirdDimension) {
        this.selectedDimensions.third = firstAvailableThirdDimension.value
      } else {
        this.selectedDimensions.third = this.thirdDimensions[0].value
      }
    },
    selectFirstAvailableColor() {
      const firstAvailableColor = this.colorsDimension.find((c) => c.available)

      if (firstAvailableColor) {
        this.selectedDimensions.color = firstAvailableColor.value

        if (this.hasThirdDimension && !this.canSelectThirdDimension) {
          this.selectedDimensions.third =
            firstAvailableColor[this.thirdDimensionName]
        }
      } else if (this.colorsDimension.length > 0) {
        this.selectedDimensions.color = this.colorsDimension[0].value

        if (this.hasThirdDimension && !this.canSelectThirdDimension) {
          this.selectedDimensions.third =
            this.colorsDimension[0][this.thirdDimensionName]
        }
      }
    },
    selectFirstAvailableSize() {
      let firstAvailableSize = this.sizesDimension.find((c) => c.available)
      if (!firstAvailableSize) {
        firstAvailableSize = this.sizesDimension[0]
      }

      this.selectedDimensions.size = firstAvailableSize.opt.size.value
    },
    async onColorChange() {
      if (this.hasThirdDimension) {
        if (!this.canSelectThirdDimension) {
          const color = this.productDocument.colors.find((c) =>
            c._key.endsWith(this.selectedDimensions.color)
          )
          if (color) {
            this.selectedDimensions.third = this.getThirdFromColorKey(
              color._key
            )
          }
        } else if (this.sizesDimension.length === 0) {
          const cc = this.shopifyProduct.getDimensionsByDimension(
            'color',
            this.selectedDimensions.color,
            this.thirdDimensionName
          )
          this.selectedDimensions.third = cc[0]
        }

        await this.$nextTick()
      }

      if (this.selectedDimensions.size !== '') {
        const variantSize = this.sizesDimension.find(
          (s) => s.opt.size.value === this.selectedDimensions.size
        )

        if (variantSize) {
          this.selectedDimensions.size = variantSize.opt.size.value
        } else {
          this.selectFirstAvailableSize()
        }
      }

      await this.$nextTick()

      this.$track.product(
        this.productDocument.title,
        this.shopifyProduct,
        this.sizesDimension.length > 0 ? this.sizesDimension : []
      )

      this.updateState(true)
    },
    async onThirdDimensionChange() {
      if (this.sizesDimension.length === 0) {
        this.selectFirstAvailableColor()
      }

      await this.$nextTick()

      this.$emit(
        'update-dimension',
        this.thirdDimensionName,
        this.selectedDimensions.third
      )

      this.updateState(true)
    },
    onLinkedProductChange(uid) {
      this.$router.push({
        path: this.$contextSanityPath({ _type: 'product', uid }),
        query: { color: this.selectedDimensions.color },
      })
    },
    updateState(fromUserAction) {
      if (this.selectedDimensions.size !== '') {
        this.errors.size = ERRORS_TYPE.NONE
      }

      if (this.hasThirdDimension && this.canSelectThirdDimension) {
        this.$emit(
          'update-color',
          this.selectedDimensions.color,
          fromUserAction,
          this.selectedDimensions.third
        )
      } else {
        this.$emit(
          'update-color',
          this.selectedDimensions.color,
          fromUserAction,
          this.selectedDimensions.third
        )
      }

      if (fromUserAction && this.selectedVariant) {
        this.$emit('update-variant', this.selectedVariant)
        this.$track.selectVariant(
          this.productDocument.title,
          this.shopifyProduct,
          this.selectedVariant,
          this.shopifyProduct.currency
        )
      }
    },
    track() {
      this.$track.selectVariant(
        this.productDocument.title,
        this.shopifyProduct,
        this.selectedVariant,
        this.shopifyProduct.currency
      )
    },
    exchange() {
      this.$emit('show-modal', 'exchange')
    },
    sizeGuide() {
      this.$track.click('size-guide')
      this.$emit('show-modal', 'size-guide', this.selectedDimensions.size)
    },
    isElIntoView(el) {
      const rect = el.getBoundingClientRect()

      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      )
    },
    addToCart() {
      if (this.selectedDimensions.size === '') {
        this.errors.size = ERRORS_TYPE.EMPTY

        if (
          this.$refs.sizePicker &&
          !this.isElIntoView(this.$refs.sizePicker.$el)
        ) {
          this.$refs.sizePicker.$el.scrollIntoView()
        }

        return
      }

      const customAttributes = []
      customAttributes.push({ key: '_order_type', value: this.orderType })
      customAttributes.push({
        key: '_delivery',
        value: this.$t(this.deliveryText.ref, this.deliveryText.options),
      })
      customAttributes.push({
        key: '_delivery_last_updated_at',
        value: new Date().toJSON(),
      })
      customAttributes.push({
        key: '_slug',
        value: this.productDocument.uid,
      })
      customAttributes.push({ key: '_locale', value: this.$i18n.locale })
      customAttributes.push({ key: '_basket_date', value: new Date().toJSON() })
      if (this.productDocument.care_guide) {
        customAttributes.push({
          key: '_guide-entretien',
          value: this.productDocument.care_guide.uid,
        })
      }
      if (this.delivery && !isNaN(this.delivery.getTime())) {
        customAttributes.push({
          key: '_delivery_date',
          value: this.delivery,
        })
      }

      if (this.productHasBeenAccessedFromUpsell) {
        customAttributes.push({
          key: '_upsell',
          value: 'true',
        })
      }

      const cartItem = {
        merchandiseId: ShopifyBase64.encode(
          SHOPIFY_TYPES.VARIANT,
          this.selectedVariant.id
        ),
        quantity: 1,
        attributes: customAttributes,
      }

      this.addItemToCart([cartItem])
        .then((_) => {
          this.$track.addToCart(
            this.productDocument.title,
            this.shopifyProduct,
            this.selectedVariant,
            this.shopifyProduct.currency,
            this.cart
          )

          // AB Tasty - Si AB Test sur le slider du produit et de la couleur
          if (this.sliderAbtestColor) {
            this.$track.addToCartAbTestColor(
              this.productDocument.uid,
              slugify(this.sliderAbtestColor)
            )
          }

          document
            .getElementById('linkOpenCart')
            .dispatchEvent(new Event('click'))
        })
        .catch((err) => {
          this.$logError.captureException(err)
          this.errors.addToCart = ERRORS_TYPE.FAIL
        })

      if (this.authenticated) {
        return services.laPosteService.addToCart(
          this.customer.email,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }

      if (this.connectedEmail && this.connectedEmail !== '') {
        return services.laPosteService.addToCart(
          this.connectedEmail,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }
    },
    initKleep() {
      const s = document.createElement('script')
      s.type = 'text/javascript'
      s.async = true
      s.src = 'https://module.kleep.ai/api/widget'
      s.id = 'kleepWidget'
      s.onload = () => {
        this.runKleep()
      }
      document.body.appendChild(s)
    },
    runKleep() {
      if (typeof window.kleep !== 'undefined') {
        kleep.load({
          domain: 'asphalte.com',
          lang: this.$i18n.locale.slice(0, 2),
          selectors: {
            sizing: '#kleep-module-sizing',
          },
          product_id: `${this.shopifyProduct.id}`,
          getSizes: () => {
            const shopifyVariants = this.shopifyProduct.variants
            return shopifyVariants
              .sort((a, b) => {
                const orderedColors = this.colorsDimension.map((c) => c.value)
                if (orderedColors.length > 0) {
                  if (
                    orderedColors.indexOf(a.opt.color.value) <
                    orderedColors.indexOf(b.opt.color.value)
                  )
                    return -1
                  if (
                    orderedColors.indexOf(a.opt.color.value) >
                    orderedColors.indexOf(b.opt.color.value)
                  )
                    return 1
                }
                return 0
              })
              .map((v) => ({
                color: v.opt.color.value,
                label: v.opt.size.value,
                variantId: `${v.id}`,
                quantity: this.productIsOnSale() ? v.available : 0,
              }))
          },
          addToCart: (variantId) => {
            const v = this.shopifyProduct.variants.find(
              (v) => v.id === parseInt(variantId)
            )
            if (v) {
              this.selectedDimensions.size = v.opt.size.value
              this.updateState(false)
              this.addToCart()
            }
          },
          selectSize: (variantId) => {
            const v = this.shopifyProduct.variants.find(
              (v) => v.id === parseInt(variantId)
            )
            if (v) {
              this.selectedDimensions.size = v.opt.size.value
              this.updateState(false)
            }
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
.purchase {
  .action {
    width: 100%;
  }

  &__bottom {
    transition: margin 0.3s;
  }

  &__size-picker {
    &.fade-leave-to {
      height: 0;
      overflow: hidden;
    }
  }

  .almost-sold-out {
    font-family: var(--secondary-font-family);
    letter-spacing: 0.02em;
  }

  #kleep-module-sizing {
    --link-underline-transform: scaleX(0);
    --link-underline-origin: 100% 50%;

    position: relative;
    font-size: var(--button-font-size-secondary);
    color: var(--secondary-color);
    font-weight: 600;
    line-height: 1;
    text-transform: uppercase;

    .kleep-button,
    .kleep-texts {
      color: var(--secondary-color) !important;
    }

    .kleep-default {
      font-size: var(--button-font-size-secondary) !important;
      color: var(--secondary-color);
    }

    .kleep-underline {
      padding-bottom: 0;

      &::after {
        background: currentcolor;
        bottom: auto;
        top: 100%;
        transform: var(--link-underline-transform);
        transform-origin: var(--link-underline-origin);
        transition: transform 0.8s;
        transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
      }
    }

    &:hover,
    &:focus {
      --link-underline-transform: scaleX(1);
      --link-underline-origin: 0% 50%;
    }
  }

  .size-guide-highlighted {
    #kleep-module-sizing {
      &::before {
        content: '';
        position: absolute;
        inset: #{rem(-3px)} #{rem(-5px)};
        background-color: var(--yellow-color);
      }
    }
  }
}
</style>
